import React from "react";
import { Badge } from "components/Graphs/Badge";
import { cx } from "components/Graphs/utils";
import {
  AGENT_LAUNCHABLE_IDS,
  DEFAULT_HERMES_DEPLOY_URL,
  DEFAULT_OPENCLAW_DEPLOY_URL,
  getAgentLaunchableDeployNowHref,
} from "constants/agentLaunchables";
import { GridBackground } from "./grid-background";
import { HoneycombBackground } from "./HoneycombBackground";
import { MarketplaceLaunchableCardWrapper } from "./marketplace-launchable-card";
import { PhysicalAISection } from "./physical-ai-section";
import ApplicationPageLayout from "components/ApplicationShell/ApplicationPageLayout";

type AgentLaunchable = {
  name: string;
  description: string;
  launchableId?: string;
  deployUrlOverride?: string;
  fallbackDeployUrl?: string;
  topBadgeText?: string;
};

const BASE_AGENT_LAUNCHABLES: AgentLaunchable[] = [
  {
    name: "OpenClaw",
    description:
      "Bring up OpenClaw on a Brev VM with the agent service and code-server ready to open after deployment.",
    launchableId: AGENT_LAUNCHABLE_IDS.openclaw,
    deployUrlOverride: getAgentLaunchableDeployNowHref(
      AGENT_LAUNCHABLE_IDS.openclaw,
    ),
    fallbackDeployUrl: DEFAULT_OPENCLAW_DEPLOY_URL,
  },
  {
    name: "NemoClaw",
    description:
      "Deploy the NemoClaw reference stack for running OpenClaw assistants with policy-based privacy and security guardrails.",
    launchableId: AGENT_LAUNCHABLE_IDS.nemoclaw,
    deployUrlOverride: getAgentLaunchableDeployNowHref(
      AGENT_LAUNCHABLE_IDS.nemoclaw,
    ),
  },
  {
    name: "Hermes",
    description:
      "Launch Hermes on Brev with a ready-to-run agent environment and compute included.",
    launchableId: AGENT_LAUNCHABLE_IDS.hermes,
    deployUrlOverride: getAgentLaunchableDeployNowHref(
      AGENT_LAUNCHABLE_IDS.hermes,
    ),
    fallbackDeployUrl: DEFAULT_HERMES_DEPLOY_URL,
  },
];

function StaticAgentCard({ agent }: { agent: AgentLaunchable }) {
  const isAvailable = Boolean(agent.fallbackDeployUrl);

  return (
    <GridBackground
      deployUrl={agent.fallbackDeployUrl}
      disableCursor={!isAvailable}
      disableScale={!isAvailable}
    >
      <div
        className={cx(
          "border border-zinc-800 bg-primaryBg p-4 transition-all duration-300 h-[220px] flex flex-col justify-between rounded-md",
          isAvailable
            ? "cursor-pointer hover:shadow-lg"
            : "cursor-default opacity-80",
        )}
      >
        <div className="h-full">
          <div className="mb-2 flex items-center justify-between">
            <Badge
              variant={isAvailable ? "info" : "neutral"}
              className="text-xs"
            >
              {isAvailable ? "Agent" : "Coming soon"}
            </Badge>
          </div>

          <div>
            <h1 className="text-lg text-primary transition-colors">
              {agent.name}
            </h1>
            <p className="text-sm text-secondary">by Brev</p>
            <div className="mt-4">
              <p className="line-clamp-2 text-sm text-secondary">
                {agent.description}
              </p>
            </div>
          </div>
        </div>

        <div className="pt-0">
          <p className="text-sm font-semibold text-secondary">
            {isAvailable
              ? "Open launchable details"
              : "Launchable not created yet"}
          </p>
        </div>
      </div>
    </GridBackground>
  );
}

function AgentLaunchableSection({
  title,
  launchables,
}: {
  title: string;
  launchables: AgentLaunchable[];
}) {
  return (
    <section>
      <div className="mb-6 flex items-center gap-3">
        <h2 className="text-xl font-bold text-primary">{title}</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {launchables.map((agent) =>
          agent.launchableId ? (
            <MarketplaceLaunchableCardWrapper
              key={agent.name}
              environment={{
                environment_id: agent.launchableId,
                name: agent.name,
              }}
              topBadge={{
                text: agent.topBadgeText ?? "Agent",
                variant: "info",
              }}
              deployUrlOverride={agent.deployUrlOverride}
            />
          ) : (
            <StaticAgentCard key={agent.name} agent={agent} />
          ),
        )}
      </div>
    </section>
  );
}

const AgentsPage: React.FC = () => {
  return (
    <div className="relative w-full overflow-hidden">
      <HoneycombBackground />

      <ApplicationPageLayout className="relative z-10 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h1 className="mb-4 bg-gradient-to-r from-white via-white to-gray-400 bg-clip-text text-2xl font-bold text-transparent">
              Agents
            </h1>
            <p className="text-sm leading-6 text-secondary">
              Start from a proven agent stack instead of a blank slate. Brev
              Launchables handle the compute and setup so you can get to a
              running agent in minutes.
            </p>
          </div>
        </div>

        <div className="mx-auto px-4">
          <div className="space-y-12">
            <AgentLaunchableSection
              title="Base Agents"
              launchables={BASE_AGENT_LAUNCHABLES}
            />
            <PhysicalAISection
              deployPath="/launchable/deploy/now"
              showLearnMore
            />
          </div>
        </div>
      </ApplicationPageLayout>
    </div>
  );
};

export default AgentsPage;
